import { useCallback, useState } from 'react';

import { formatDate } from '../lib/format';
import { useAdminData } from '../lib/use-admin-data';
import {
  cleanupLoadTestData,
  fetchLoadTestFootprint,
  type LoadTestFootprint,
} from '../services/api';
import { Feedback } from './Feedback';
import { StatTile } from './StatTile';

/**
 * Aba TESTE DE CARGA — o rastro que o teste deixou e o botão de limpar.
 *
 * O que define "rastro do teste" é a marca `is_synthetic`, gravada no momento
 * da requisição a partir do cabeçalho `X-Load-Test`. A limpeza apaga
 * exatamente essas linhas — nunca "as mensagens da conta de teste", critério
 * que levaria dado real junto no dia em que alguém usasse a conta de verdade.
 */

const REFRESH_MS = 10_000;

type CleanupTarget = 'messages' | 'accesses' | 'all';

const CLEANUP_LABEL: Record<CleanupTarget, string> = {
  messages: 'as mensagens do teste',
  accesses: 'os acessos do teste',
  all: 'tudo que o teste gerou',
};

export function AdminLoadTestTab() {
  const loader = useCallback(() => fetchLoadTestFootprint(), []);
  const { data, error, isLoading, reload } = useAdminData<LoadTestFootprint>(loader, REFRESH_MS);

  const [pendingTarget, setPendingTarget] = useState<CleanupTarget | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [isCleaning, setIsCleaning] = useState(false);

  async function confirmCleanup(target: CleanupTarget) {
    setIsCleaning(true);
    setResult(null);
    try {
      const removed = await cleanupLoadTestData(target);
      setResult(
        `Removidos ${removed.deletedMessages.toLocaleString('pt-BR')} registros de mensagem e ` +
          `${removed.deletedAccessLogs.toLocaleString('pt-BR')} de acesso.`,
      );
      await reload();
    } catch (caught) {
      setResult(caught instanceof Error ? caught.message : 'Falha na limpeza.');
    } finally {
      setIsCleaning(false);
      setPendingTarget(null);
    }
  }

  if (error) return <Feedback kind="error">{error}</Feedback>;
  if (isLoading && !data) return <p className="page-loading">Carregando…</p>;

  const hasData = (data?.syntheticAccessLogs ?? 0) + (data?.syntheticMessages ?? 0) > 0;

  return (
    <div className="admin-tab">
      <div className="stat-grid">
        <StatTile
          hero
          label="Acessos sintéticos guardados"
          value={(data?.syntheticAccessLogs ?? 0).toLocaleString('pt-BR')}
          detail="requisições marcadas com o cabeçalho do teste"
        />
        <StatTile
          label="Mensagens sintéticas"
          value={(data?.syntheticMessages ?? 0).toLocaleString('pt-BR')}
          detail="não entram no financeiro de nenhum criador"
        />
        <StatTile
          label="Primeiro registro"
          value={data?.firstSeen ? formatDate(data.firstSeen) : '—'}
        />
        <StatTile
          label="Último registro"
          value={data?.lastSeen ? formatDate(data.lastSeen) : '—'}
        />
      </div>

      <section className="admin-section">
        <h3 className="admin-section__title">Como o tráfego do teste é reconhecido</h3>
        <ul className="admin-section__list">
          <li>
            O runner envia <code>X-Load-Test: &lt;segredo&gt;</code> em toda requisição; o segredo
            vive no <code>LOAD_TEST_SECRET</code> da API.
          </li>
          <li>
            Requisição com o segredo correto entra marcada como sintética — e é isso que permite ler
            o painel com e sem o teste, no seletor de tráfego lá em cima.
          </li>
          <li>
            Mensagens criadas pelo teste <strong>não disparam</strong> webhooks nem o bot da Twitch:
            o teste é seu, os serviços de terceiros não pediram para participar.
          </li>
          <li>
            O cabeçalho <code>X-Load-Test-Origin</code> (ex.: <code>aws:sa-east-1</code>) separa as
            regiões dos runners na aba Público.
          </li>
        </ul>
      </section>

      <section className="admin-section">
        <h3 className="admin-section__title">Limpar os dados do teste</h3>
        <p className="admin-section__description">
          Ação destrutiva e sem volta. Apaga apenas o que está marcado como sintético.
        </p>

        {result && <Feedback kind="success">{result}</Feedback>}

        {pendingTarget ? (
          <div className="admin-confirm">
            <p>
              Confirma apagar <strong>{CLEANUP_LABEL[pendingTarget]}</strong>? Não dá para desfazer.
            </p>
            <div className="admin-confirm__actions">
              <button
                className="button button--danger"
                type="button"
                disabled={isCleaning}
                onClick={() => void confirmCleanup(pendingTarget)}
              >
                {isCleaning ? 'Apagando…' : 'Sim, apagar'}
              </button>
              <button
                className="button button--ghost"
                type="button"
                onClick={() => setPendingTarget(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div className="admin-actions">
            <button
              className="button button--ghost"
              type="button"
              disabled={!hasData}
              onClick={() => setPendingTarget('messages')}
            >
              Apagar mensagens do teste
            </button>
            <button
              className="button button--ghost"
              type="button"
              disabled={!hasData}
              onClick={() => setPendingTarget('accesses')}
            >
              Apagar acessos do teste
            </button>
            <button
              className="button button--danger"
              type="button"
              disabled={!hasData}
              onClick={() => setPendingTarget('all')}
            >
              Apagar tudo do teste
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
